<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import AppButton from '@/components/v2/AppButton.vue';
import Store from '@/store';
import { UnauthorizedReason } from '@/types/AuthFailure';

const SUPPORT_EMAIL = 'platform@cacgroup.com';

const props = defineProps<{
  reason: UnauthorizedReason;
}>();

interface DeadEndCopy {
  heading: string;
  body: string;
  action: { label: string; run: () => void };
  supportSubject: string;
}

const router = useRouter();
const heading = ref<HTMLHeadingElement>();
const signedInEmail = computed(() => Store.state.user?.email);

const copy = computed<DeadEndCopy>(() => {
  switch (props.reason) {
    case 'no-role':
      return {
        heading: "Your account doesn't have access yet",
        body: "You're signed in, but your account hasn't been given access to the CAC Platform. Ask the Platform team to set up your access, and include the email address you signed in with.",
        action: { label: 'Sign out', run: () => router.push('/logout') },
        supportSubject: 'Platform access request',
      };
    case 'unrecognized':
      return {
        heading: "We couldn't find your Platform account",
        body: "You signed in, but that account isn't set up for the CAC Platform, or it has been deactivated. If you have more than one work account, try signing in with a different one.",
        action: { label: 'Sign in with a different account', run: () => router.push('/logout') },
        supportSubject: 'Platform account not recognized',
      };
    case 'unavailable':
      return {
        heading: "We couldn't load your account",
        body: 'Something went wrong on our side while loading your account. This is usually temporary, so try again in a moment.',
        // A full reload, not a route change: the account is only loaded while the app starts.
        action: { label: 'Try again', run: () => window.location.assign('/') },
        supportSubject: 'Platform account failed to load',
      };
    default:
      return {
        heading: 'Unauthorized',
        body: "You don't have access to this page. If you need it for your work, ask the Platform team for access.",
        action: { label: 'Go home', run: () => router.push('/') },
        supportSubject: 'Platform page access request',
      };
  }
});

const supportHref = computed(() => `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(copy.value.supportSubject)}`);

// SPA route changes don't move focus, so a keyboard or screen reader user would otherwise
// be left on whatever they last interacted with, with no sign the page changed.
onMounted(() => heading.value?.focus());
</script>

<template>
  <div class="mx-auto flex max-w-xl flex-col items-center gap-4 px-4 pt-[15vh] text-center">
    <h1
      ref="heading"
      tabindex="-1"
      class="outline-none"
      data-test="title">
      {{ copy.heading }}
    </h1>
    <p>{{ copy.body }}</p>
    <p
      v-if="reason === 'no-role' && signedInEmail"
      class="text-sm">
      Signed in as <strong>{{ signedInEmail }}</strong>
    </p>
    <AppButton
      primary
      @click="copy.action.run">
      {{ copy.action.label }}
    </AppButton>
    <p class="text-sm">
      Need help? Contact <a :href="supportHref">{{ SUPPORT_EMAIL }}</a>.
    </p>
  </div>
</template>
